import { useLayoutEffect, useRef, useState } from "react";

/** Height guess for rows that are not rendered yet (min-h-7.5) */
const ESTIMATED_ITEM_HEIGHT = 30;
export const MIN_VISIBLE_ROWS_COUNT = 4;

type ListOptions = {
  totalCount: number;
  isExpanded?: boolean;
  minVisibleCount?: number;
};

type ListKey = "docs" | "chats";

type MeasuredList = {
  totalCount: number;
  isExpanded: boolean;
  containerRef: React.RefObject<HTMLElement | null>;
  itemHeights: number[];
  visibleCount: number;
};

const withoutSingleHiddenRow = (count: number, totalCount: number) =>
  totalCount - count === 1 ? totalCount : count;

function fitInto(list: MeasuredList, budget: number) {
  let usedHeight = 0;
  let count = 0;
  while (count < list.totalCount) {
    const itemHeight = list.itemHeights[count] ?? ESTIMATED_ITEM_HEIGHT;
    if (usedHeight + itemHeight > budget) break;
    usedHeight += itemHeight;
    count += 1;
  }
  return { count, usedHeight };
}

function useMeasuredList<Container extends HTMLElement>(
  { totalCount, isExpanded = false, minVisibleCount = 0 }: ListOptions,
  fitCount: number,
) {
  const containerRef = useRef<Container | null>(null);
  const itemHeightsRef = useRef<number[]>([]);

  return {
    totalCount,
    isExpanded,
    containerRef,
    itemHeights: itemHeightsRef.current,
    visibleCount: withoutSingleHiddenRow(Math.max(fitCount, minVisibleCount), totalCount),
  };
}

const EMPTY_LIST: ListOptions = { totalCount: 0 };

function scrollContainerOf(element: HTMLElement) {
  let ancestor = element.parentElement;
  while (ancestor && !/auto|scroll/.test(getComputedStyle(ancestor).overflowY)) {
    ancestor = ancestor.parentElement;
  }
  return ancestor;
}

/**
 * Computes how many rows of the docs and chats lists fit into the scroll
 * container vertically, so that everything inside `containerToFitRef` stays
 * visible without scrolling. The content below `containerToFitRef` (trash,
 * create team) is allowed to go under the scroll.
 *
 * The scroll container is the nearest ancestor that scrolls vertically, so
 * wrappers such as the tree root can sit in between and grow with their rows.
 */
export function useVisibleRowsCounts({
  docs = EMPTY_LIST,
  chats = EMPTY_LIST,
  enabled = true,
}: {
  docs?: ListOptions;
  chats?: ListOptions;
  enabled?: boolean;
}) {
  const containerToFitRef = useRef<HTMLDivElement | null>(null);
  const [fitCounts, setFitCounts] = useState<Record<ListKey, number>>({
    docs: MIN_VISIBLE_ROWS_COUNT,
    chats: MIN_VISIBLE_ROWS_COUNT,
  });

  const lists = {
    docs: useMeasuredList<HTMLUListElement>(docs, fitCounts.docs),
    chats: useMeasuredList<HTMLUListElement>(chats, fitCounts.chats),
  };
  const listsRef = useRef(lists);
  listsRef.current = lists;

  useLayoutEffect(() => {
    const containerToFit = containerToFitRef.current;
    const scrollContainer = containerToFit && scrollContainerOf(containerToFit);
    const listEntries = Object.entries(listsRef.current) as [ListKey, MeasuredList][];
    const isEveryListExpanded = listEntries.every(([, list]) => list.isExpanded);
    if (!enabled || isEveryListExpanded || !containerToFit || !scrollContainer) return undefined;

    const measure = () => {
      const measured = listEntries.map(([key, list]) => {
        const renderedCount = list.isExpanded
          ? list.totalCount
          : Math.min(list.visibleCount, list.totalCount);
        const rows = Array.from(list.containerRef.current?.children ?? []).slice(0, renderedCount);
        let rowsHeight = 0;
        rows.forEach((row, index) => {
          const { height } = row.getBoundingClientRect();
          list.itemHeights[index] = height;
          rowsHeight += height;
        });
        return { key, list, rowsHeight };
      });

      const renderedHeight = measured.reduce((sum, { rowsHeight }) => sum + rowsHeight, 0);
      const nonRowsHeight = containerToFit.getBoundingClientRect().height - renderedHeight;
      let budget = scrollContainer.clientHeight - nonRowsHeight;

      measured.forEach(({ list, rowsHeight }) => {
        if (list.isExpanded) budget -= rowsHeight;
      });
      const collapsedLists = measured.filter(
        ({ list }) => !list.isExpanded && list.containerRef.current,
      );

      const evenShare = budget / collapsedLists.length;
      const fits = collapsedLists.map(({ key, list }) => ({
        key,
        list,
        fit: fitInto(list, evenShare),
      }));
      let leftover = budget - fits.reduce((sum, { fit }) => sum + fit.usedHeight, 0);
      fits.forEach((entry) => {
        const extendedFit = fitInto(entry.list, entry.fit.usedHeight + leftover);
        leftover -= extendedFit.usedHeight - entry.fit.usedHeight;
        entry.fit = extendedFit;
      });

      setFitCounts((prev) => {
        const next = { ...prev };
        fits.forEach(({ key, fit }) => {
          next[key] = Math.max(MIN_VISIBLE_ROWS_COUNT, fit.count);
        });
        return next.docs === prev.docs && next.chats === prev.chats ? prev : next;
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scrollContainer);
    observer.observe(containerToFit);

    return () => observer.disconnect();
  }, [
    enabled,
    lists.docs.totalCount,
    lists.docs.isExpanded,
    lists.docs.visibleCount,
    lists.chats.totalCount,
    lists.chats.isExpanded,
    lists.chats.visibleCount,
  ]);

  return {
    containerToFitRef,
    docsContainerRef: lists.docs.containerRef,
    chatsContainerRef: lists.chats.containerRef,
    visibleDocsCount: lists.docs.isExpanded ? undefined : lists.docs.visibleCount,
    visibleChatsCount: lists.chats.isExpanded ? undefined : lists.chats.visibleCount,
  };
}
